import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Recursion.module.css';
import FactorialEngine from '../recursionengine/FactorialEngine';
import { RecursionCommands } from '../../../types/RecursionCommands';

export const FACTORIAL_CODE = `function factorial(n) {
  if (n <= 1) {
    return 1;
  }
  return n * factorial(n - 1);
}`;

interface FactorialWrapperProps {
  command?: RecursionCommands | null;
  onComplete?: () => void;
}

const FactorialWrapper: React.FC<FactorialWrapperProps> = ({ 
  command: externalCommand, 
  onComplete 
}) => {
  const navigate = useNavigate();
  const [internalCommand, setInternalCommand] = useState<RecursionCommands | null>(null);
  const [inputValue, setInputValue] = useState('6');
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [finalResult, setFinalResult] = useState<number | null>(null);

  useEffect(() => {
    if (externalCommand) {
      setInternalCommand(externalCommand);
      
      if (externalCommand.action === 'generate') {
        const randomValue = Math.floor(Math.random() * 7) + 2;
        setInputValue(randomValue.toString());
      }
      
      if (externalCommand.action === 'reset') {
        setFinalResult(null);
      }
    }
  }, [externalCommand]);

  const handleRun = () => {
    if (isAnimating) return;
    setInternalCommand({ id: Date.now(), action: 'run' });
  };

  const handleStop = () => {
    if (!isAnimating) return;
    setInternalCommand({ id: Date.now(), action: 'stop' });
  };

  const handleReset = () => {
    setInternalCommand({ id: Date.now(), action: 'reset' });
    setFinalResult(null);
  };

  const handleGenerate = () => {
    if (isAnimating) return;
    const randomValue = Math.floor(Math.random() * 7) + 2;
    setInputValue(randomValue.toString());
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
    setActiveLine(null);
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>
      <h1 className={styles.title}>Factorial</h1>

      <div className={styles.description}>
        <h2>Description</h2>
        <p>
          A factorial, denoted by n!, is the product of all positive integers up
          to n. For example, 5! = 5 × (4 × (3 × (2 × (1)))) = 120. It's a classic
          example for teaching recursion with a clear base case (0! = 1) and a
          recursive step (n! = n × (n - 1)!).
        </p>
      </div>

      <div className={styles.useCase}>
        <h2>Use Cases</h2>
        <p>
          Factorials are commonly used in combinatorics to calculate
          permutations and combinations. For instance, "How many ways can you
          arrange 5 books on a shelf?" The answer is 5! = 120.
        </p>
      </div>

      <div className={styles.timeComplexity}>
        <h2>Time Complexity</h2>
        <p>
          The time complexity is <b>O(n)</b> since the function calls itself n
          times, and the space complexity is also <b>O(n)</b> due to the
          recursion stack depth.
        </p>
      </div> 

      <div className={styles.controls}>
        <input
          type="number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={isAnimating}
          min="0"
          max="12"
        />
        <button onClick={handleRun} disabled={isAnimating}>Run</button>
        <button onClick={handleGenerate} disabled={isAnimating}>Generate</button>
        <button onClick={handleStop}>Stop</button>
        <button onClick={handleReset}>Reset</button>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.vizAndCode}>
          <FactorialEngine
            command={internalCommand}
            onComplete={handleAnimationComplete}
            inputValue={inputValue}
            setActiveLine={setActiveLine}
            isAnimating={isAnimating}
            setIsAnimating={setIsAnimating}
            setFinalResult={setFinalResult}
            finalResult={finalResult}
          />

          <div className={styles.codeDisplay}>
            {FACTORIAL_CODE.split('\n').map((line, index) => (
              <div
                key={index}
                className={`${styles.codeLine} ${activeLine === index ? styles.highlightedCode : ''}`}
              >
                {line || ' '}
              </div>
            ))}
            {finalResult !== null && (
              <div className={styles.finalResultLine}>Result: {finalResult}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FactorialWrapper;